import { KlineType, KlinePeriod } from "../types/kline";
import { SubscribeType, SymbolType } from "../types/socket";
const STRICT_SYMBOL_REGEX = /^[A-Za-z0-9]+\$[A-Za-z0-9]+$/;

export function buildApiUrl(baseURL: string, path: string): string {
    return `${baseURL.replace(/\/$/, '')}${path}`;
}

export function buildWebSocketUrl(wssURL: string, path: string): string {
    return `${wssURL.replace(/\/$/, '')}${path}`;
}

/**
 * Convert K-line period string to number
 */
export function convertKlinePeriod(period: KlineType): number {
    if (period === KlinePeriod.ONE_MIN) return 1;
    if (period === KlinePeriod.FIVE_MIN) return 2;
    if (period === KlinePeriod.FIFTEEN_MIN) return 3;
    if (period === KlinePeriod.THIRTY_MIN) return 4;
    if (period === KlinePeriod.ONE_HOUR) return 5;
    if (period === KlinePeriod.TWO_HOUR) return 6;
    if (period === KlinePeriod.FOUR_HOUR) return 7;
    if (period === KlinePeriod.ONE_DAY) return 8;
    if (period === KlinePeriod.ONE_WEEK) return 9;
    if (period === KlinePeriod.ONE_MONTH) return 10;
    return period as number;
}

/**
 * Convert websocket subscription type
 */

export function convertSubscribeType(type: SubscribeType): string {
    if (type === SubscribeType.KLINE_1M) return 'kline@1';
    if (type === SubscribeType.KLINE_5M) return 'kline@2';
    if (type === SubscribeType.KLINE_15M) return 'kline@3';
    if (type === SubscribeType.KLINE_30M) return 'kline@4';
    if (type === SubscribeType.KLINE_1H) return 'kline@5';
    if (type === SubscribeType.KLINE_2H) return 'kline@6';
    if (type === SubscribeType.KLINE_4H) return 'kline@7';
    if (type === SubscribeType.KLINE_1D) return 'kline@8';
    if (type === SubscribeType.KLINE_1W) return 'kline@9';
    if (type === SubscribeType.KLINE_1MTH) return 'kline@10';
    return type;
}

/**
 * Convert subscription type list
 */

export function parseeSubscribeType(type: string | SubscribeType[]): string {
    let rawList: string[];
    if (Array.isArray(type)) rawList = type.map(String);
    else rawList = type.split(',');
    const validTypes = Object.values(SubscribeType);
    const processedList: string[] = [];
    const apiToEnumMap: Record<string, SubscribeType> = {};
    for (const enumValue of validTypes) {
        // Ensure enumValue is a valid SubscribeType
        if (typeof enumValue === 'string' || typeof enumValue === 'number') {
            const apiFormat = convertSubscribeType(enumValue as SubscribeType);
            apiToEnumMap[apiFormat] = enumValue as SubscribeType;
        }
    }
    for (const item of rawList) {
        const trimmed = item.trim() as SubscribeType;
        if (!trimmed) continue;
        let targetEnum: SubscribeType | undefined;
        const isValid = validTypes.some(v => v === trimmed);
        if (isValid) targetEnum = trimmed;
        else targetEnum = apiToEnumMap[trimmed];
        if (!targetEnum) throw new Error(`Invalid subscribe type: "${trimmed}". Valid types are: ${validTypes.join(', ')}`);
        processedList.push(convertSubscribeType(trimmed as SubscribeType));
    }
    return [...new Set(processedList)].join(',');
}


/**
 * Subscription product parameter formatting function
 * @param input Subscription parameters
 */
export function parseSymbols(input: SymbolType[] | string): string {
    const list = Array.isArray(input) ? input : input.split(',');
    return [...new Set(
        list
            .map(item => item.trim())
            .filter(Boolean)
            .map(item => {
                if (!STRICT_SYMBOL_REGEX.test(item)) throw new Error(`Invalid symbol format: "${item}". Expected format is "SYMBOL$REGION", e.g. "AAPL$US".`);
                return item.toUpperCase();
            })
    )].join(',');
}

/**
 * HTTP Request Options Interface
 */
export interface HttpRequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    headers?: Record<string, string>;
    body?: string;
    params?: Record<string, any>;
}

/**
 * HTTP Response Interface
 */
export interface HttpResponse {
    status: number;
    data: any;
    headers: Record<string, string>;
}

/**
 * Build query string
 */
function buildQueryString(params: Record<string, any>): string {
    return Object.entries(params)
        .filter(([_, value]) => value != null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
}

/**
 * HTTP request implementation for Browser environment (using fetch API)
 */
export async function makeHttpRequest(url: string, options: HttpRequestOptions = {}): Promise<HttpResponse> {
    const {
        method = 'GET',
        headers = {},
        body,
        params = {},
    } = options;

    // Build complete URL
    let finalUrl = url;
    // For GET requests, append params to URL
    if (method === 'GET' && Object.keys(params).length > 0) {
        const queryString = buildQueryString(params);
        // Handle existing query parameters in the base URL
        const separator = finalUrl.includes('?') ? '&' : '?';
        finalUrl = `${finalUrl}${separator}${queryString}`;
    }

    // Prepare fetch options
    const fetchOptions: RequestInit = {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...headers,
        },
    };

    // Attach body for non-GET methods
    if (body && method !== 'GET') {
        fetchOptions.body = body;
    }

    try {
        const response = await fetch(finalUrl, fetchOptions);

        // Extract headers as a plain object
        const responseHeaders: Record<string, string> = {};
        response.headers.forEach((value, key) => {
            responseHeaders[key] = value;
        });

        // Parse JSON response
        let data: any;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            try {
                data = await response.json();
            } catch (e) {
                // Fallback to text if JSON parsing fails but content-type suggests JSON
                data = await response.text();
            }
        } else {
            // For non-JSON responses, get text
            data = await response.text();
        }

        return {
            status: response.status,
            data,
            headers: responseHeaders,
        };
    } catch (error) {
        // Network errors or other fetch failures
        throw new Error(`Request failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}

/**
 * Create WebSocket instance (Browser environment)
 */
export async function createWebSocket(url: string, token?: string): Promise<WebSocket> {
    return new WebSocket(`${url}?token=${token}`,);
}


